import { useState } from 'react';
import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useEmailAccounts } from '@/hooks/email/useEmailAccounts';
import { EmailAccountListItem } from './EmailAccountListItem';
import { EmailAccountForm } from './EmailAccountForm';
import { EmailAccount, EmailAccountInput } from '@/services/api/emailAccounts';
import { EmailAccountFormData } from '@/schemas/emailAccountSchema';
import { Plus, ArrowLeft, Loader2, Mail } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

interface EmailAccountsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ViewMode = 'list' | 'add' | 'edit';

export const EmailAccountsDialog = ({
  open,
  onOpenChange,
}: EmailAccountsDialogProps) => {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedAccount, setSelectedAccount] = useState<EmailAccount | null>(null);

  const {
    accounts,
    isLoading,
    createAccount,
    updateAccount,
    deleteAccount,
    isCreating,
    isUpdating,
    isDeleting,
    refetch,
  } = useEmailAccounts();

  // Refetch accounts when dialog opens
  React.useEffect(() => {
    if (open) {
      refetch();
    }
  }, [open, refetch]);

  const handleAddNew = () => {
    setSelectedAccount(null);
    setViewMode('add');
  };

  const handleEdit = (account: EmailAccount) => {
    setSelectedAccount(account);
    setViewMode('edit');
  };

  const handleDelete = async (accountId: number) => {
    await deleteAccount(accountId);
  };

  // Build a provider-aware payload — Hostinger accounts only send the token + basics,
  // IMAP accounts send the full IMAP/SMTP configuration.
  const buildPayload = (data: EmailAccountFormData): EmailAccountInput => {
    const base: EmailAccountInput = {
      provider: data.provider,
      email_address: data.email_address,
      display_name: data.display_name,
      signature_html: data.signature_html || undefined,
    };

    if (data.provider === 'hostinger') {
      // Token is write-only — only send when the user entered one
      if (data.hostinger_token?.trim()) {
        base.hostinger_token = data.hostinger_token.trim();
      }
      return base;
    }

    return {
      ...base,
      imap_server: data.imap_server,
      imap_port: data.imap_port,
      imap_username: data.imap_username,
      imap_password: data.imap_password,
      imap_mailbox: data.imap_mailbox,
      smtp_server: data.smtp_server,
      smtp_port: data.smtp_port,
      smtp_username: data.smtp_username,
      smtp_password: data.smtp_password,
      use_ssl: data.use_ssl,
      trust_self_signed: data.trust_self_signed,
      max_sync_per_run: data.max_sync_per_run,
      sync_window_days: data.sync_window_days,
      sync_delete_email_from_server: data.sync_delete_email_from_server,
    };
  };

  const handleSubmit = async (data: EmailAccountFormData) => {
    const payload = buildPayload(data);
    if (viewMode === 'edit' && selectedAccount?.id) {
      await updateAccount(selectedAccount.id, payload);
    } else {
      await createAccount(payload);
    }
    setViewMode('list');
    setSelectedAccount(null);
  };

  const handleCancel = () => {
    setViewMode('list');
    setSelectedAccount(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {viewMode !== 'list' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                className="h-7 w-7 p-0"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
              </Button>
            )}
            {viewMode === 'list' && 'Email Account Settings'}
            {viewMode === 'add' && 'Add Email Account'}
            {viewMode === 'edit' && 'Edit Email Account'}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {viewMode === 'list' && 'Manage your email accounts for sending and receiving messages.'}
            {viewMode === 'add' && 'Configure a new email account with IMAP and SMTP settings.'}
            {viewMode === 'edit' && 'Update your email account configuration.'}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[calc(85vh-8rem)] pr-4">
          <div className="mt-2">
          {viewMode === 'list' && (
            <>
              <div className="mb-4">
                <Button onClick={handleAddNew} className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Account
                </Button>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : accounts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No email accounts configured yet.</p>
                  <p className="text-sm">Add your first account to get started.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {accounts.map((account) => (
                    <EmailAccountListItem
                      key={account.id}
                      account={account}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      isDeleting={isDeleting}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {(viewMode === 'add' || viewMode === 'edit') && (
            <EmailAccountForm
              account={selectedAccount || undefined}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isSubmitting={isCreating || isUpdating}
            />
          )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default EmailAccountsDialog;
